import axios from 'axios';
import { returnErrors } from './errorActions';
import { getUserXP } from './xpActions';

import {
  USER_LOADED,
  USER_LOADING,
  AUTH_ERROR,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT_SUCCESS,
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAIL,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAIL,
  CLEAR_PASSWORD_MESSAGE,
  ACCESS_SUCCESS,
  ACCESS_FAIL,
  START_COURSE_SUCCESS,
  NEXT_LESSON_SUCCESS,
  NEXT_CONTENT_SUCCESS,
  NEXT_CONTENT_REQUEST,
  COURSE_PROGRESSION_ERROR,
  PROGRESS_LOADING,
  PROGRESS_LOADED,
  PROGRESS_NOT_FOUND,
  RESET_PROGRESS_SUCCESS,
  RESET_PROGRESS_FAIL,
  START_IMPERSONATION,
  END_IMPERSONATION,
  VERIFY_EMAIL_SUCCESS,
  VERIFY_EMAIL_FAIL,
  CLEAR_REGISTRATION_DATA,
  NAVIGATION_CONTEXT_LOADED,
  COURSE_CONTENT_LOADED
} from './types';

// Bootstrap student — loads full path/courses/progress in one heavy call
// Only called from StudentDashboard, not on every auth check
export const bootstrapStudent = () => (dispatch, getState) => {
  dispatch({ type: PROGRESS_LOADING });

  axios
    .get('/api/auth/bootstrap', tokenConfig(getState))
    .then(res => {
      const { progress, ...userData } = res.data;
      dispatch({ type: USER_LOADED, payload: userData });
      if (progress) {
        dispatch({ type: PROGRESS_LOADED, payload: progress });
      } else {
        dispatch({ type: PROGRESS_NOT_FOUND });
      }
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      dispatch({ type: COURSE_PROGRESSION_ERROR });
    });
};

// Check token & load user
export const loadUser = () => (dispatch, getState) => {
  // User loading
  dispatch({ type: USER_LOADING });

  axios
    .get('/api/auth', tokenConfig(getState))
    .then(res => {
      dispatch({
        type: USER_LOADED,
        payload: res.data
      });
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data || { msg: 'Network error' }, err.response?.status || 500));
      dispatch({
        type: AUTH_ERROR
      });
    });
};

// Access Visitor
export const access = ({ name, email, password }) => dispatch => {
  // Headers
  const config = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  // Request body
  const body = JSON.stringify({ name, email, password });

  axios
    .post('api/users/register-visitor', body, config)
    .then(res =>
      dispatch({
        type: ACCESS_SUCCESS,
        payload: res.data
      })
    )
    .catch(err => {
      dispatch(
        returnErrors(err.response.data, err.response.status, 'ACCESS_FAIL')
      );
      dispatch({
        type: ACCESS_FAIL
      });
    });
};

// Verify Email for Registration
export const verifyEmail = (email) => dispatch => {
  const config = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  const body = JSON.stringify({ email });

  axios
    .post('/api/users/verify-email', body, config)
    .then(res => {
      dispatch({
        type: VERIFY_EMAIL_SUCCESS,
        payload: res.data
      });
    })
    .catch(err => {
      // Check if it's a "no invitation" response
      const responseData = err.response?.data;
      if (responseData?.noInvitation) {
        // Pass through the noInvitation flag
        dispatch({
          type: VERIFY_EMAIL_FAIL,
          payload: { noInvitation: true, email: responseData.email }
        });
      } else {
        dispatch(
          returnErrors(
            responseData?.msg || 'Email verification failed',
            err.response?.status || 400,
            'VERIFY_EMAIL_FAIL'
          )
        );
        dispatch({
          type: VERIFY_EMAIL_FAIL
        });
      }
    });
};

// Verify Group Code for Registration
export const verifyGroupCode = (email, groupCode) => dispatch => {
  const config = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  const body = JSON.stringify({ email, groupCode });

  axios
    .post('/api/users/verify-group-code', body, config)
    .then(res => {
      dispatch({
        type: VERIFY_EMAIL_SUCCESS,
        payload: res.data
      });
    })
    .catch(err => {
      dispatch(
        returnErrors(
          err.response?.data?.msg || 'Group code verification failed',
          err.response?.status || 400,
          'VERIFY_EMAIL_FAIL'
        )
      );
      dispatch({
        type: VERIFY_EMAIL_FAIL
      });
    });
};

// Clear Registration Data
export const clearRegistrationData = () => {
  return {
    type: CLEAR_REGISTRATION_DATA
  };
};

// Register User
export const register = ({ name, lastName, email, country, birthYear, password, groupCode }) => dispatch => {
  // Headers
  const config = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  // Request body - include groupCode if provided
  const body = JSON.stringify({ name, lastName, email, country, birthYear, password, groupCode });

  axios
    .post('/api/users/register-user', body, config)
    .then(res => {
      dispatch({
        type: REGISTER_SUCCESS,
        payload: res.data
      });
      // Load user data after successful registration
      dispatch(loadUser());
    })
    .catch(err => {
      dispatch(
        returnErrors(
          err.response?.data?.msg || err.response?.data || 'Registration failed',
          err.response?.status || 400,
          'REGISTER_FAIL'
        )
      );
      dispatch({
        type: REGISTER_FAIL
      });
    });
};

// Self-register from external website (no invitation required)
export const selfRegister = ({ email, programId, referralCode, pathwayId }) => dispatch => {
  const config = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  const body = JSON.stringify({ email, programId, referralCode, pathwayId });

  axios
    .post('/api/users/self-register', body, config)
    .then(res => {
      dispatch({
        type: REGISTER_SUCCESS,
        payload: res.data
      });
      dispatch(loadUser());
    })
    .catch(err => {
      dispatch(
        returnErrors(
          err.response?.data?.msg || err.response?.data || 'Registration failed',
          err.response?.status || 400,
          'REGISTER_FAIL'
        )
      );
      dispatch({
        type: REGISTER_FAIL
      });
    });
};

// Login User
export const login = ({ email, password }) => dispatch => {
  // Headers
  const config = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  // Request body
  const body = JSON.stringify({ email, password });

  axios
    .post('/api/auth', body, config)
    .then(res => {
      dispatch({
        type: LOGIN_SUCCESS,
        payload: res.data
      });
      
      // Load user data after successful login
      dispatch(loadUser());
    })
    .catch(err => {
      console.error('Login error:', err);
      
      // Extract error message safely
      let errorData = 'Login failed. Please try again.';
      let errorStatus = 500;
      
      if (err.response) {
        errorStatus = err.response.status;
        if (err.response.data) {
          errorData = err.response.data;
        }
      } else if (err.message) {
        errorData = err.message;
      }
      
      dispatch(
        returnErrors(errorData, errorStatus, 'LOGIN_FAIL')
      );
      dispatch({
        type: LOGIN_FAIL
      });
    });
};

// Forgot Password
export const forgotPassword = ({ email }) => dispatch => {
  // Headers
  const config = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  // Request body
  const body = JSON.stringify({ email });

  axios
    .post('/api/auth/forgot-password', body, config)
    .then(res => {
      dispatch({
        type: FORGOT_PASSWORD_SUCCESS,
        payload: res.data
      });
    })
    .catch(err => {
      console.error('Forgot password error:', err);
      
      // Extract error message safely
      let errorData = 'Failed to send password reset email. Please try again.';
      let errorStatus = 500;
      
      if (err.response) {
        errorStatus = err.response.status;
        if (err.response.data) {
          errorData = err.response.data;
        }
      } else if (err.message) {
        errorData = err.message;
      }
      
      dispatch(
        returnErrors(errorData, errorStatus, 'FORGOT_PASSWORD_FAIL')
      );
      dispatch({
        type: FORGOT_PASSWORD_FAIL
      });
    });
};

// Logout User
export const logout = () => {
  return {
    type: LOGOUT_SUCCESS
  };
};

// Student progress actions
export const startCourse = () => (dispatch, getState) => {
  dispatch({ type: PROGRESS_LOADING });

  axios
    .get('/api/auth/start-course', tokenConfig(getState))
    .then(res => {
      dispatch({
        type: START_COURSE_SUCCESS,
        payload: res.data
      });
      // Refresh navigation context so Previous button reflects new position
      dispatch(getNavigationContext());
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data || { msg: 'Network error' }, err.response?.status || 500));
      dispatch({
        type: COURSE_PROGRESSION_ERROR
      });
    });
};

// Next content (lesson or submission form)
export const nextContent = (skipReinforcement = false) => (dispatch, getState) => {
  // Check if a request is already in progress
  const state = getState();
  if (state.auth.isContentProgressing) {
    return;
  }

  dispatch({ type: NEXT_CONTENT_REQUEST });
  dispatch({ type: PROGRESS_LOADING });

  // Build URL with optional skipReinforcement query parameter
  const url = skipReinforcement
    ? '/api/auth/next-lesson?skipReinforcement=true'
    : '/api/auth/next-lesson';

  axios
    .get(url, tokenConfig(getState))
    .then(res => {
      dispatch({
        type: NEXT_CONTENT_SUCCESS,
        payload: res.data
      });
      // Refresh navigation context so Previous button reflects new position
      dispatch(getNavigationContext());
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data || { msg: 'Network error' }, err.response?.status || 500));
      dispatch({
        type: COURSE_PROGRESSION_ERROR
      });
    });
};

// Next lesson (kept for backward compatibility)
export const nextLesson = () => (dispatch, getState) => {
  return nextContent()(dispatch, getState);
};

// Save progress without completing lesson
export const saveProgress = () => (dispatch, getState) => {
  dispatch({ type: PROGRESS_LOADING });
  
  axios
    .post('/api/auth/save-progress', {}, tokenConfig(getState))
    .then(res => {
      dispatch({
        type: PROGRESS_LOADED,
        payload: res.data.progress
      });
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data || { msg: 'Network error' }, err.response?.status || 500));
      dispatch({
        type: COURSE_PROGRESSION_ERROR
      });
    });
};

// Get detailed progress information
export const getProgress = () => (dispatch, getState) => {
  dispatch({ type: PROGRESS_LOADING });

  axios
    .get('/api/auth/progress', tokenConfig(getState))
    .then(res => {
      if (res.data.code === 'NO_PROGRESS') {
        dispatch({ type: PROGRESS_NOT_FOUND });
      } else {
        dispatch({ type: PROGRESS_LOADED, payload: res.data });
      }
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      dispatch({ type: COURSE_PROGRESSION_ERROR });
    });
};

// Get cross-course navigation context
export const getNavigationContext = () => (dispatch, getState) => {
  axios
    .get('/api/auth/navigation-context', tokenConfig(getState))
    .then(res => {
      dispatch({
        type: NAVIGATION_CONTEXT_LOADED,
        payload: res.data
      });
    })
    .catch(() => {
      // Silently fail — navigation context is non-critical and may not exist yet
    });
};

// Reset progress and onboarding for a user
export const resetProgress = () => (dispatch, getState) => {
  dispatch({ type: PROGRESS_LOADING });
  
  axios
    .delete('/api/auth/reset-progress', tokenConfig(getState))
    .then(res => {
      // Update user data with the reset onboarding status
      if (res.data.user) {
        dispatch({
          type: USER_LOADED,
          payload: res.data.user
        });
      }
      
      dispatch({
        type: RESET_PROGRESS_SUCCESS,
        payload: res.data
      });
      
      // Refresh XP data since it was also reset
      dispatch(getUserXP());
    })
    .catch(err => {
      dispatch(
        returnErrors(err.response.data, err.response.status, 'RESET_PROGRESS_FAIL')
      );
      dispatch({
        type: RESET_PROGRESS_FAIL
      });
    });
};

// Reset progress and onboarding for a specific user (admin only)
export const resetUserProgress = (userId) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    dispatch({ type: PROGRESS_LOADING });
    
    axios
      .delete(`/api/auth/reset-user-progress/${userId}`, tokenConfig(getState))
      .then(res => {
        dispatch({
          type: RESET_PROGRESS_SUCCESS,
          payload: res.data
        });
        resolve(res.data);
      })
      .catch(err => {
        dispatch(
          returnErrors(err.response.data, err.response.status, 'RESET_PROGRESS_FAIL')
        );
        dispatch({
          type: RESET_PROGRESS_FAIL
        });
        reject(err);
      });
  });
};

// Start impersonating a user (admin only)
export const startImpersonation = (userId) => (dispatch, getState) => {
  const state = getState();
  const currentUser = state.auth.user;
  
  // Only allow admins to impersonate
  if (currentUser.role !== 'admin' && currentUser.role !== 'platform_admin') {
    dispatch(returnErrors({ msg: 'Only admins can impersonate users' }, 403));
    return;
  }

  axios
    .post('/api/auth/impersonate', { userId }, tokenConfig(getState))
    .then(res => {
      // Store the new impersonation token
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('originalUserId', res.data.originalUserId);
      
      dispatch({
        type: START_IMPERSONATION,
        payload: {
          impersonatedUser: res.data.user,
          originalUserId: res.data.originalUserId,
          originalUser: res.data.originalUser,
          token: res.data.token
        }
      });
      
      // Reload user XP for the impersonated user
      dispatch(getUserXP());
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data || { msg: 'Network error' }, err.response?.status || 500));
    });
};

// End impersonation and return to original user
export const endImpersonation = () => (dispatch, getState) => {
  const state = getState();
  
  if (!state.auth.isImpersonating) {
    dispatch(returnErrors({ msg: 'No impersonation session found' }, 400));
    return;
  }

  axios
    .post('/api/auth/end-impersonation', {}, tokenConfig(getState))
    .then(res => {
      // Restore the original admin token
      localStorage.setItem('token', res.data.token);
      localStorage.removeItem('originalUserId');
      
      dispatch({
        type: END_IMPERSONATION,
        payload: {
          user: res.data.user,
          token: res.data.token
        }
      });
      
      // Reload user XP for the original admin user
      dispatch(getUserXP());
    })
    .catch(err => {
      dispatch(returnErrors(err.response?.data || { msg: 'Network error' }, err.response?.status || 500));
    });
};

// Change Password
export const changePassword = ({ currentPassword, newPassword }) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    // Request body
    const body = JSON.stringify({ currentPassword, newPassword });

    axios
      .post('/api/auth/change-password', body, tokenConfig(getState))
      .then(res => {
        dispatch({
          type: CHANGE_PASSWORD_SUCCESS,
          payload: res.data
        });
        resolve(res.data);
      })
      .catch(err => {
        console.error('Change password error:', err);
        
        // Extract error message safely
        let errorData = 'Failed to change password. Please try again.';
        let errorStatus = 500;
        
        if (err.response) {
          errorStatus = err.response.status;
          if (err.response.data) {
            errorData = err.response.data;
          }
        } else if (err.message) {
          errorData = err.message;
        }
        
        dispatch(
          returnErrors(errorData, errorStatus, 'CHANGE_PASSWORD_FAIL')
        );
        dispatch({
          type: CHANGE_PASSWORD_FAIL
        });
        reject(err);
      });
  });
};

// Clear password change message
export const clearPasswordMessage = () => dispatch => {
  dispatch({
    type: CLEAR_PASSWORD_MESSAGE
  });
};

// Lazy-load full course content (lessons/submissionForms/reinforcements) on course click.
// Results are cached in Redux — subsequent clicks are instant.
export const fetchCourseContent = (courseId) => async (dispatch, getState) => {
  const cached = getState().auth.courseDetails[courseId];
  if (cached) return cached;

  try {
    const res = await axios.get(`/api/courses/${courseId}/content`, tokenConfig(getState));
    dispatch({ type: COURSE_CONTENT_LOADED, payload: { courseId, course: res.data } });
    return res.data;
  } catch (err) {
    throw err;
  }
};

// Setup config/headers and token
export const tokenConfig = getState => {
  // Get token from localstorage
  const token = getState().auth.token;

  // Headers
  const config = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  // If token, add to headers
  if (token) {
    config.headers['Authorization'] = 'Bearer ' + token;
  }

  return config;
};
